import { useState, useEffect } from 'react';
import {
  ButtonPrimary,
  ButtonPseudo,
  Checkbox,
  Select,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { useAppSelector } from '@store';
import { TariffStatus, TariffAction } from '../../hoc/tariffs/types';
import { PublishSidepanelTypes } from './types';

import cn from './tariffs.module.css';

const PublishSidepanel = ({
  panelIsOpen,
  setPanelIsOpen,
  status,
  onStatusChange,
  statusChangeSuccess,
  statusChangeInProgress,
  tariffId,
}: PublishSidepanelTypes) => {
  const [selectedOption, setSelectedOption] = useState(TariffAction.NONE);
  const [selectedTariff, setSelectedTariff] = useState('');

  const {
    tariffs: {
      tariffPage: { tariffs },
      list: { tariffs: tariffsList },
    },
    configurationDictionaries: { riskScoreRanges },
  } = useAppSelector((state) => state);

  let sidedpanelText;

  if (selectedOption === TariffAction.NONE) {
    sidedpanelText =
      'Tariff will be published but will not be applied to customers';
  } else if (selectedOption === TariffAction.REPLACE_ALL) {
    sidedpanelText =
      'Tariff will be replaced for all customers with this tariff';
  } else if (selectedOption === TariffAction.REPLACE_ALL_AND_ARCHIVE_PREVIOUS) {
    sidedpanelText =
      'Tariff will be replaced for all customers with this tariff, and this tariff will be archived';
  } else if (selectedOption === TariffAction.REPLACE_WITHOUT_CUSTOM_FEES) {
    sidedpanelText =
      'Tariff will be replaced for all customers, except for those whose this tariff has been customized';
  }

  useEffect(() => {
    if (statusChangeSuccess) {
      setPanelIsOpen(false);
      setSelectedTariff('');
      setSelectedOption(TariffAction.NONE);
    }
  }, [statusChangeSuccess]);

  return (
    <SidePanel
      type={SidePanelType.Fees}
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={() => setPanelIsOpen(false)}
      title="Publish options"
      sidepanelExpandedKey="publish-tariff"
      footer={
        <>
          <ButtonPrimary
            text={statusChangeInProgress ? 'Loading...' : 'Publish tariff'}
            onClick={() =>
              onStatusChange({
                status,
                previousTariffId: selectedTariff,
                action: selectedOption,
              })
            }
            disabled={Boolean(
              selectedOption !== TariffAction.NONE && !selectedTariff,
            )}
            bold
          />
          <ButtonPseudo
            text="Close"
            onClick={() => setPanelIsOpen(false)}
            bold
          />
        </>
      }
    >
      <>
        <div className={cn.optionRow}>
          <Checkbox
            name="option"
            type="radio"
            title="Publish without replacement"
            checked={Boolean(selectedOption === TariffAction.NONE)}
            onChange={() => setSelectedOption(TariffAction.NONE)}
            visualClassName={cn.optionRadio}
          />
        </div>
        <div className={cn.optionRow}>
          <Checkbox
            name="option"
            type="radio"
            title="Replace tariff for all customers"
            checked={Boolean(selectedOption === TariffAction.REPLACE_ALL)}
            onChange={() => setSelectedOption(TariffAction.REPLACE_ALL)}
            visualClassName={cn.optionRadio}
          />
        </div>
        <div className={cn.optionRow}>
          <Checkbox
            name="option"
            type="radio"
            title="Replace and archive tariff for all customers"
            checked={Boolean(
              selectedOption === TariffAction.REPLACE_ALL_AND_ARCHIVE_PREVIOUS,
            )}
            onChange={() =>
              setSelectedOption(TariffAction.REPLACE_ALL_AND_ARCHIVE_PREVIOUS)
            }
            visualClassName={cn.optionRadio}
          />
        </div>
        <div className={cn.optionRow}>
          <Checkbox
            name="option"
            type="radio"
            title="Replace tariff for all customers, except for customized ones"
            checked={Boolean(
              selectedOption === TariffAction.REPLACE_WITHOUT_CUSTOM_FEES,
            )}
            onChange={() =>
              setSelectedOption(TariffAction.REPLACE_WITHOUT_CUSTOM_FEES)
            }
            visualClassName={cn.optionRadio}
          />
        </div>
        <div className={cn.sidepanelSeparator} />
        <div className={cn.sidepanelText}>{sidedpanelText}</div>
        {selectedOption !== TariffAction.NONE && (
          <div className={cn.sidepanelSelectWrapper}>
            <Select
              list={
                tariffs
                  ?.filter((item) => item.value !== tariffId)
                  .map((item) => {
                    const body: {
                      name: string;
                      value: string;
                      description?: string;
                    } = {
                      name: item.name,
                      value: item.value as string,
                    };

                    const currentRiskScore = riskScoreRanges?.find(
                      (range) => range.id === item.description,
                    );

                    if (currentRiskScore) {
                      body.description = `Risk score ${currentRiskScore.rangeStart} - ${currentRiskScore.rangeEnd} (${currentRiskScore.name})`;
                    }

                    return body;
                  }) ||
                tariffsList
                  ?.filter(
                    (item) =>
                      item.status !== TariffStatus.DRAFT &&
                      item.status !== TariffStatus.ARCHIVED,
                  )
                  .filter((item) => item.id !== tariffId)
                  .map((item) => {
                    const body: {
                      name: string;
                      value: string;
                      description?: string;
                    } = {
                      name: item.name,
                      value: item.id,
                    };

                    const currentRiskScore = riskScoreRanges?.find(
                      (range) => range.id === item.riskScoreRangeId,
                    );

                    if (currentRiskScore) {
                      body.description = `Risk score ${currentRiskScore.rangeStart} - ${currentRiskScore.rangeEnd} (${currentRiskScore.name})`;
                    }

                    return body;
                  })
              }
              placeholder="Select tariff..."
              name="selectedTariff"
              value={selectedTariff}
              onChange={(e) => setSelectedTariff(e.target.value as string)}
              title="Replaceable tariff"
            />
          </div>
        )}
      </>
    </SidePanel>
  );
};

export default PublishSidepanel;
